import PropTypes from 'prop-types';
import React, { Component } from 'sitewide-react';
import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';
import NewCitationPing from './NewCitationPing';
import { trackingExtra, trackingSources } from './mentionPingTracking';

class RedesignedMentionsPing extends Component {
  constructor(props) {
    super(props);
    this.closeRef = React.createRef();
  }

  // Both arms report the same three events with the same two extras; only the source prefix
  // differs. Owning this in the base class means every ping built on the template is
  // instrumented identically, so control and variant are directly comparable.
  trackingSources() {
    return trackingSources(this.props.newDesign);
  }

  trackingExtra() {
    return trackingExtra({
      isCitation: this.props.isCitation,
      citingAuthorName: this.props.citingAuthorName,
    });
  }

  createCloseTooltip(pingName) {
    return (e) => {
      e.preventDefault();
      trackClick({
        source: this.trackingSources().dismiss,
        extra: this.trackingExtra(),
      });
      $.ajax({
        type: 'POST',
        url: $r.record_ping_dismissal_api_v0_onsite_pings_path({
          subdomain_param: 'api',
        }),
        contentType: 'application/json',
        processData: false,
        data: JSON.stringify({
          ping_name: pingName,
        }),
      });

      const pingContainer = document.getElementById('onsite-ping');
      pingContainer.classList.remove('visible');

      let event;
      if (typeof Event === 'function') {
        event = new Event('dismiss');
      } else {
        event = document.createEvent('Event');
        event.initEvent('dismiss', true, true);
      }
      pingContainer.dispatchEvent(event);
    };
  }

  getBody() {
    // Not implemented
  }

  getUpgradeURL() {
    // Not Implemented
  }

  getButtonText() {
    return 'View Your Mentions ▸';
  }

  renderIcon() {
    if (this.props.profilePictureUrl) {
      return (
        <div
          className="profile-picture-wrapper"
          style={{ backgroundImage: `url(${this.props.profilePictureUrl})` }}
        />
      );
    } else {
      return (
        <div className="icon-wrapper">
          <img src={$image_url('onsite_pings/mentions-icon.png')} />
        </div>
      );
    }
  }

  renderUserInfo() {
    return (
      <div className="user-info">
        <div className="user-name">{this.props.userDisplayName}</div>
        <div className="affiliation">{this.props.userAffiliation}</div>
      </div>
    );
  }

  renderTopSection() {
    return (
      <div className="ping-header">
        <div className="close-button-wrapper">
          <button
            ref={this.closeRef}
            className="close-button"
            onClick={this.createCloseTooltip(this.props.pingName)}
          >
            ×
          </button>
        </div>
        <div className="icon-and-info-wrapper">
          {this.renderIcon()}
          {this.renderUserInfo()}
        </div>
      </div>
    );
  }

  renderMainSection() {
    return (
      <div className="ping-main">
        <div className="title">MENTIONS UPDATE</div>
        {this.getBody()}
      </div>
    );
  }

  redirectToUpgradeUrl = () => {
    trackClick({
      source: this.trackingSources().ctaClick,
      extra: this.trackingExtra(),
    });
    $.ajax({
      type: 'POST',
      url: $r.record_ping_clickthrough_api_v0_onsite_pings_path({
        subdomain_param: 'api',
      }),
      contentType: 'application/json',
      processData: false,
      data: JSON.stringify({
        ping_name: this.props.pingName,
      }),
    });

    window.location = this.getUpgradeURL();
  };

  onBodyClick = (event) => {
    if (event.target === this.closeRef.current) return;
    this.redirectToUpgradeUrl(); // TODO Replace with anchor tag.
  };

  renderButton() {
    return (
      <div className="ping-button">
        <button className="Button Button--round">{this.getButtonText()}</button>
      </div>
    );
  }

  renderBasePing() {
    return (
      <div
        className="redesigned-ping-wrapper u-clickable"
        onClick={this.onBodyClick}
      >
        {this.renderTopSection()}
        {this.renderMainSection()}
        {this.renderButton()}
      </div>
    );
  }

  renderMobilePing() {
    return <div className="mobile-popover">{this.renderBasePing()}</div>;
  }

  // growth_2026_q3_new_citation_pings. The swap lives here rather than in each subclass so
  // every ping built on this template picks it up, and closing the test to the variant means
  // deleting the legacy branch below rather than touching five components. The new design
  // derives its own copy from the mention, so getBody and getUpgradeURL overrides in
  // subclasses only matter for the control arm and the upgrade destination respectively.
  renderNewDesignPing() {
    return (
      <NewCitationPing
        isCitation={this.props.isCitation}
        citingAuthorName={this.props.citingAuthorName}
        mentionedName={this.props.mentionedName}
        topic={this.props.researchInterestName}
        onClose={this.createCloseTooltip(this.props.pingName)}
        onCtaClick={this.redirectToUpgradeUrl}
        // The new card is fixed-position on phones, which stops an observer wrapped around it
        // from firing, so it mounts the watcher inside its own content stack instead.
        impressionSource={this.trackingSources().impression}
        impressionExtra={this.trackingExtra()}
      />
    );
  }

  renderLegacyPing() {
    return $a.isSmallScreen() ? this.renderMobilePing() : this.renderBasePing();
  }

  render() {
    if (this.props.newDesign) {
      return this.renderNewDesignPing();
    }

    return (
      <ImpressionWatcher
        source={this.trackingSources().impression}
        extra={this.trackingExtra()}
      >
        {this.renderLegacyPing()}
      </ImpressionWatcher>
    );
  }
}

RedesignedMentionsPing.propTypes = {
  pingName: PropTypes.string.isRequired,
  userDisplayName: PropTypes.string.isRequired,
  userAffiliation: PropTypes.string,
  profilePictureUrl: PropTypes.string,
  newDesign: PropTypes.bool,
  isCitation: PropTypes.bool,
  citingAuthorName: PropTypes.string,
  mentionedName: PropTypes.string,
  researchInterestName: PropTypes.string,
};

export default RedesignedMentionsPing;
